import { ApplicationView, useController } from '@aha-app/mvc';
import classnames from 'classnames';
import React, { useState } from 'react';
import { pluralize } from '@aha-app/aha-shared';
import { type RecordId } from '../../../types';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import VotingSessionController from './controllers/VotingSessionController';
import { PokerCard } from './PokerCard';
import styles from './VotingSession.module.css';
import { LocateButton } from './PokerDetails';

const ResultItem = ({
  value,
  users,
  isSelected,
  onClick,
}: {
  value: string;
  users: string[];
  isSelected: boolean;
  onClick: (() => void) | undefined;
}) => {
  return (
    <div className={styles.pokerResultItem}>
      <div className={styles.pokerResultItemTopRow}>
        <div className='grow'>
          <PokerCard
            cardSize='small'
            value={value}
            cardColor={isSelected ? 'selected' : 'normal'}
            onClick={onClick}
          />
        </div>
        {onClick && (
          <aha-button kind='secondary' outline onClick={onClick}>
            Select
          </aha-button>
        )}
        <div className={styles.pokerResultItemVoteCount}>
          {users.length} {pluralize(users.length, 'vote')}
        </div>
      </div>
      <div className={styles.pokerResultItemUserList}>
        {users.sort().map(user => (
          <aha-pill key={user} color='var(--aha-gray-200)'>
            {user}
          </aha-pill>
        ))}
      </div>
    </div>
  );
};

export const PokerResultsPanel = ApplicationView(
  ({
    widget,
    recordId,
    userIsFacilitator,
  }: {
    widget: VotingSessionWidget;
    recordId: RecordId;
    userIsFacilitator: boolean;
  }) => {
    const controller = useController(VotingSessionController);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const allResults = widget.votesByObjectAndUser();
    const objectResults = allResults[recordId] ?? {};
    const resultsByChoice: Record<string, string[]> = {};

    Object.entries(objectResults).forEach(([userId, choice]) => {
      const userName = controller.getResolvedUser(userId as RecordId);
      if (!resultsByChoice[choice]) {
        resultsByChoice[choice] = [userName];
      } else {
        resultsByChoice[choice].push(userName);
      }
    });
    const pokerChoices = widget.pokerChoices;
    if (!pokerChoices) {
      throw new Error(
        'pokerChoices should not be undefined in a poker session'
      );
    }

    const currentVotingObject = controller.getCurrentVotingObject();
    if (!currentVotingObject) {
      throw new Error(
        'currentVotingObject must be defined when viewing results'
      );
    }

    /* HACK: In planning poker the user's vote "count" represents the index of the chosen card in the
     set of planning poker choices. To avoid changing the existing logic for other kinds of voting
     (where zero may be treated as "hasn't voted") we store the index of the chosen value offset by +1. 
     Eg given the following choices `["S", "M", "L"]` a vote for `"S"` would have a "count"
     of 1 rather than it's array index of 0, "M" would be 2 etc... 
     Accordingly, we need to adjust for the offset here to make sure that a "count" of 2 shows up as the second
     item in the array of poker choices, not the third.
     If I had more time I think it would be worth factoring out the different kinds of voting to avoid all the conditional
     logic around which kind of voting session it is, and to store and count votes in a way that makes sense for the specific
     kind of voting rather than trying to have a general-purpose data structure.
     */

    const choicesWithNoVotes = pokerChoices.filter(
      (_choice, index) => !resultsByChoice[index + 1]
    );

    return (
      <>
        <div className={classnames('mb-2', styles.flexAlignCenter)}>
          <div
            className={classnames('color-tertiary', styles.flexAlignCenter)}
            style={{ flexGrow: 1 }}
          >
            <i className='fa-regular fa-eye mr-2' />
            <div>Results are visible</div>
          </div>
          <div>
            <LocateButton recordId={currentVotingObject.recordId} />
          </div>
        </div>
        <div className={styles.pokerScoreSummaries}>
          {Object.keys(resultsByChoice)
            .sort((choice1, choice2) =>
              resultsByChoice[choice1].length < resultsByChoice[choice2].length
                ? 1
                : -1
            )
            .map(choice => {
              const value = pokerChoices[+choice - 1];
              return (
                <ResultItem
                  key={choice}
                  isSelected={selectedValue === value}
                  onClick={
                    userIsFacilitator
                      ? () => setSelectedValue(value)
                      : undefined
                  }
                  value={value}
                  users={resultsByChoice[choice]}
                />
              );
            })}
          {choicesWithNoVotes.length && (
            <div className={styles.pokerScoreSummariesUnselectedCards}>
              <div>Unselected</div>
              <div
                className={classnames(styles.pokerCardList, 'p-2')}
                style={{ justifyContent: 'unset' }}
              >
                {choicesWithNoVotes.map(choice => (
                  <div className='mr-2' key={choice}>
                    <PokerCard
                      cardSize='small'
                      cardColor={
                        selectedValue === choice ? 'selected' : 'normal'
                      }
                      onClick={
                        userIsFacilitator
                          ? () => setSelectedValue(choice)
                          : undefined
                      }
                      value={choice}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {userIsFacilitator && (
          <>
            <div className='pt-5'>
              {selectedValue ? (
                <aha-button
                  kind='secondary'
                  size='large'
                  style={{ width: '100%' }}
                  onClick={() => {
                    controller.actionSetSelectedObject(null);
                    controller.actionSetFinalObjectScore(
                      recordId,
                      selectedValue
                    );
                  }}
                >
                  Submit
                </aha-button>
              ) : (
                <div className='t-050 flex  justify-content-center align-items-center gap-1'>
                  <aha-alert type='info' style={{ width: '100%' }}>
                    Choose the final estimate to submit.
                  </aha-alert>
                </div>
              )}
            </div>
            <div className='pt-5'>
              <aha-button
                kind='secondary'
                size='large'
                style={{ width: '100%' }}
                onClick={() => {
                  controller.actionRestartVoting(recordId);
                }}
              >
                Re-estimate
              </aha-button>
            </div>
          </>
        )}
      </>
    );
  }
);
